import {
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Icon,
  Link,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useState, type FC } from "react"

import { shortenIfAddress } from "web3/helpers/address"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import { LayerPlus } from "ui/components/icons/font-awesome/LayerPlus"
import type { ExecutableCall } from "query/graphql"
import { useDevice } from "common/hooks/useDevice"
import { getChainIdParams } from "web3/helpers/chainId"
import { pluralize, simplifySignature } from "common/helpers/string"
import { ChevronDown } from "ui/components/icons/font-awesome/ChevronDown"
import { ChevronUp } from "ui/components/icons/font-awesome/ChevronUp"
import { getContractNameFromAddressBook } from "createProposal/constants/address-book"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"

type CustomActionExecutable = {
  target?: ExecutableCall["target"]
  signature?: ExecutableCall["signature"]
  decodedCalldata?: ExecutableCall["decodedCalldata"]
}

type ProposalCustomReceiptProps = {
  customAction: CustomActionExecutable
  chainId: string
  governorId: string
}

const ProposalCustomReceipt: FC<ProposalCustomReceiptProps> = ({
  customAction,
  chainId,
  governorId,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const { target: targetAddress } = customAction
  const [displayParams, setDisplayParams] = useState(false)

  const signature =
    customAction?.decodedCalldata?.signature ?? customAction.signature

  if (!targetAddress || !signature) return null

  if (!targetAddress) return null

  const signatureLabel = simplifySignature(signature)

  const { reference } = getChainIdParams(chainId)

  const blockExplorerLink = EXTERNAL_ROUTES.etherscan.address(
    reference,
    targetAddress as string,
  )

  const parameters = customAction?.decodedCalldata?.parameters

  const contractName = getContractNameFromAddressBook(
    governorId,
    targetAddress as string,
  )

  return (
    <>
      <Flex align="center" display={onLargeDevice} p={5} w="full">
        <HStack align="center" spacing={3} w="25%">
          <Icon as={LayerPlus} fill="gray.600" h={4} w={4} />
          <Text
            color="gray.600"
            fontSize="14px"
            fontWeight={500}
            lineHeight="20px"
          >
            Custom
          </Text>
        </HStack>
        <HStack w="30%">
          <Stack direction="column" spacing={0}>
            {contractName ? (
              <Text
                color="#000000"
                fontSize="14px"
                fontWeight="medium"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {contractName}
              </Text>
            ) : null}
            <HStack>
              <Text color="gray.500" fontSize="14px" fontWeight="medium">
                {shortenIfAddress(targetAddress as string)}
              </Text>
              <Link isExternal href={blockExplorerLink}>
                <Icon
                  as={ArrowUpRight}
                  color="#98A2B3"
                  h={2.5}
                  mb={0.5}
                  w={2.5}
                />
              </Link>
            </HStack>
          </Stack>
        </HStack>
        <Flex borderRadius="md" py={1} w="45%">
          <Stack align="center" direction="row" width="100%">
            {signatureLabel ? (
              <Text
                color="gray.700"
                fontSize="14px"
                fontWeight="bold"
                overflow="hidden"
                textAlign="center"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {signatureLabel}
              </Text>
            ) : null}

            <Spacer />

            {parameters && parameters?.length > 0 ? (
              <Stack align="center" direction="row" float="right" pr={4}>
                <Button
                  fontWeight="regular"
                  h={2}
                  rightIcon={
                    <Icon
                      as={displayParams ? ChevronUp : ChevronDown}
                      color="#667085"
                      h={3}
                      w={3}
                    />
                  }
                  variant="transparent"
                  w={2}
                  onClick={() => setDisplayParams(!displayParams)}
                >
                  <Text
                    color="#667085"
                    fontSize="12px"
                    fontWeight="regular"
                    textAlign="right"
                  >
                    {parameters.length}{" "}
                    {pluralize(parameters.length, "param", "params")}
                  </Text>
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Flex>
      </Flex>

      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={LayerPlus} fill="#344054" h={3} w={3} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
          >
            Custom
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Account
          </Text>
          <HStack>
            <Stack p={0} spacing={0}>
              {contractName ? (
                <Text textStyle="body.bold.md">{contractName}</Text>
              ) : null}
              <HStack p={0}>
                <Text
                  color={contractName ? "#667085" : "default"}
                  fontWeight={contractName ? "regular" : "bold"}
                  textStyle="body.regular.md"
                >
                  {shortenIfAddress(targetAddress as string)}
                </Text>
                <Link isExternal href={blockExplorerLink}>
                  <Icon
                    as={ArrowUpRight}
                    color="#98A2B3"
                    h={2.5}
                    mb={0.5}
                    w={2.5}
                  />
                </Link>
              </HStack>
            </Stack>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Method
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            {signatureLabel ? (
              <Text
                fontWeight="bold"
                overflow="hidden"
                textAlign="center"
                textOverflow="ellipsis"
                textStyle="body.bold.md"
                whiteSpace="nowrap"
              >
                {signatureLabel}
              </Text>
            ) : null}
          </Box>
        </HStack>

        {parameters && parameters?.length > 0 ? (
          <Button
            fontWeight="regular"
            variant="secondary"
            onClick={() => setDisplayParams(!displayParams)}
          >
            {displayParams ? "Hide" : "See"} params ({parameters.length})
          </Button>
        ) : null}
      </Stack>

      {displayParams && parameters && parameters?.length > 0 ? (
        <Stack mb={4} mt={1} mx={4} spacing={0.5}>
          {parameters.map((parameter, idx) => (
            <Text
              key={`param-${idx}`}
              className="clamp-text-3"
              textStyle="code"
              whiteSpace="break-spaces"
            >
              <Code wordBreak="break-word">
                {parameter.name}:{" "}
                <Text as="span" color="gray.800">
                  {parameter.value}
                </Text>
                {idx < parameters.length - 1 ? "," : ""}
              </Code>
            </Text>
          ))}
        </Stack>
      ) : null}
    </>
  )
}

export default ProposalCustomReceipt
